/**
 * Arrangement presets for a canvas: new { x, y, w } for every tile, in reading order (row by row,
 * left to right). Pure, on canvas grid units. A tile's height is never changed.
 */
import { findFreeSpot } from "./canvasFreeSpot";
import type { FreeSpotRect } from "./canvasFreeSpot";

export type LayoutPresetId = "one-column" | "two-columns" | "three-columns" | "compact";

export interface LayoutPresetOption {
  id: LayoutPresetId;
  label: string;
  description: string;
}

export const LAYOUT_PRESETS: readonly LayoutPresetOption[] = [
  { id: "one-column", label: "1 column", description: "Stack every tile full width" },
  { id: "two-columns", label: "2 columns", description: "Two tiles side by side" },
  { id: "three-columns", label: "3 columns", description: "Three tiles side by side" },
  { id: "compact", label: "Compact (fill gaps)", description: "Keep sizes, close the empty gaps" },
];

const COLUMN_COUNT: Partial<Record<LayoutPresetId, number>> = {
  "one-column": 1,
  "two-columns": 2,
  "three-columns": 3,
};

export interface PresetTile extends FreeSpotRect {
  id: string;
  /** In 2 or 3 columns: a width the tile always takes (headline numbers), or "row" to span the grid (story-wide text). */
  span?: number | "row";
  /** The narrowest the tile may be; a narrower column gives way to it. */
  minW?: number;
}

export interface PresetPlacement extends FreeSpotRect {
  id: string;
}

/** The tiles in reading order: top to bottom, then left to right; ties keep their given order. */
export const inReadingOrder = <T extends FreeSpotRect>(tiles: readonly T[]): T[] =>
  tiles
    .map((tile, index) => ({ tile, index }))
    .sort((a, b) => a.tile.y - b.tile.y || a.tile.x - b.tile.x || a.index - b.index)
    .map(({ tile }) => tile);

/**
 * Where every tile goes under `preset` on a `cols`-column grid. `step` is the grid's column step
 * (positions the server can store exactly): widths and columns snap to it.
 *
 * - N columns: each tile takes one column's width (or its `span`) and the first free spot at or
 *   below the previous tile's row, so the reading order holds. A tile whose `minW` is wider than a
 *   column takes whole columns, so no column is left too narrow for the next tile.
 * - Compact: widths stay, each tile takes the first free spot, closing the gaps.
 */
export const computeLayoutPreset = (
  preset: LayoutPresetId,
  tiles: readonly PresetTile[],
  cols: number,
  step = 1,
): PresetPlacement[] => {
  const stride = Math.max(1, Math.floor(step));
  const count = COLUMN_COUNT[preset];
  const columnWidth = count
    ? Math.max(stride, Math.floor(cols / count / stride) * stride)
    : null;
  const placed: FreeSpotRect[] = [];
  const result: PresetPlacement[] = [];
  let floor = 0;
  for (const tile of inReadingOrder(tiles)) {
    const own = Math.max(1, Math.min(tile.w, cols));
    // Side by side (2 or 3 columns), a tile's span holds; one column stacks every tile full width.
    const span = count && count > 1 ? tile.span : undefined;
    let w = own;
    if (columnWidth && span === "row") w = cols;
    else if (columnWidth && typeof span === "number") w = Math.max(1, Math.min(span, cols));
    else if (columnWidth) w = Math.min(cols, Math.ceil(Math.max(columnWidth, tile.minW ?? 0) / columnWidth) * columnWidth);
    // A barrier over the rows already passed keeps a column preset in reading order.
    const barrier: FreeSpotRect[] =
      columnWidth && floor > 0 ? [{ x: 0, y: 0, w: cols, h: floor }] : [];
    const xStep = columnWidth && span === undefined ? columnWidth : stride;
    const spot = findFreeSpot([...barrier, ...placed], w, tile.h, cols, xStep);
    const rect = { x: spot.x, y: spot.y, w, h: tile.h };
    placed.push(rect);
    floor = Math.max(floor, spot.y);
    result.push({ id: tile.id, ...rect });
  }
  return result;
};
